import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputLabel,
  TextField,
  Autocomplete,
  Chip,
  Grid,
} from '@mui/material';
import React, { memo, useEffect } from 'react';
import { CloseIcon } from '~/assets/icons';
import { useFormik } from 'formik';
import { IPrice } from '~/utils/interface/estimate';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '~/utils/constants/common';
import { handleApiError, notify } from '~/utils/helper';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import styles from './EditPriceDialog.module.scss';
import classNames from 'classnames/bind';
import { SearchIcon } from '~/assets/icons';
import { handleCreatePrice, handleEditPrice } from '~/thunks/price/pricesThunk';
import { priceActions } from '~/thunks/price/pricesSlice';

const cx = classNames.bind(styles);

type EditPriceDialogProps = {
  open: boolean;
  setOpen?: (open: boolean, needRefreshData?: boolean) => void;
  setOpenDrawer?: (open: boolean) => void;
  mode?: string;
  title?: string;
  data?: IPrice;
  children?: React.ReactNode;
};

const initialValues: IPrice = {};

const EditPriceDialog = (props: EditPriceDialogProps) => {
  const { t } = useTranslation(['admin/price']);
  const { mode = DIALOG_MODE_CREATE, data } = props;
  const dispatch = useAppDispatch();

  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    if (props.setOpen) {
      props.setOpen(open, needRefreshData);
    }
    if (props.setOpenDrawer) {
      props.setOpenDrawer(open);
    }
    form.resetForm();
  };

  const handleCancel = () => {
    handleSetOpen(false);
  };
  const { tags } = useAppSelector((state) => state.tag);

  const form = useFormik({
    validateOnChange: false,
    initialValues,
    onSubmit: async (values, { setFieldError }) => {
      try {
        const tagIds = values.tags?.map((tag) => tag.id);

        const { tags, ...otherValues } = values;

        const dataToSubmit = {
          ...otherValues,
          tagIds: tagIds,
        };

        if (mode === DIALOG_MODE_CREATE) {
          await dispatch(handleCreatePrice(dataToSubmit)).unwrap();
          dispatch(priceActions.changeNeedRefreshData(true));
          notify('success', t('createSuccess', { ns: 'admin/common' }));
        } else if (mode === DIALOG_MODE_EDIT && data?.id) {
          await dispatch(handleEditPrice({ id: data.id, params: dataToSubmit })).unwrap();

          dispatch(priceActions.changeNeedRefreshData(true));
          notify('success', t('editSuccess', { ns: 'admin/common' }));
        }
        handleSetOpen(false, true);
      } catch (e: unknown) {
        handleApiError(e, setFieldError);
      }
    },
  });
  const selectedTags = form.values.tags || [];
  useEffect(() => {
    if (mode === DIALOG_MODE_EDIT && data) {
      form.setFieldValue('name', data.name || '');
      form.setFieldValue('city', data.city || '');
      form.setFieldValue('state', data.state || '');
      form.setFieldValue('zip_code', data.zip_code || '');
      form.setFieldValue('material_sale_tax', data.material_sale_tax || '');
      form.setFieldValue('tags', data.tags || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, data]);

  return (
    <Dialog open={props.open} onClose={() => handleSetOpen(false)} fullWidth maxWidth='md'>
      <DialogTitle>
        {mode === DIALOG_MODE_CREATE ? t('addNewPrice') : t('editPrice')}
        <IconButton onClick={() => handleSetOpen(false)} className='absolute right-[10px] top-[10px]'>
          <CloseIcon className='w-[10px] h-[10px]' />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box component='form'>
          <Box className='gap-[20px] mb-[20px]'>
            <InputLabel>{t('priceName')}</InputLabel>
            <TextField
              name='name'
              placeholder={t('priceName')}
              value={form.values.name}
              onChange={form.handleChange}
              error={Boolean(form.errors.name)}
              helperText={form.errors.name}
            />
          </Box>
        </Box>
        <Box component='form'>
          <Grid container spacing={3} className=' mb-[20px]'>
            <Grid item xs={12} sm={6}>
              <InputLabel>{t('properties.city')}</InputLabel>
              <TextField
                name='city'
                placeholder={t('properties.city')}
                value={form.values.city}
                onChange={form.handleChange}
                error={Boolean(form.errors.city)}
                helperText={form.errors.city}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <InputLabel>{t('properties.state')}</InputLabel>
              <TextField
                name='state'
                placeholder={t('properties.state')}
                value={form.values.state}
                onChange={form.handleChange}
                error={Boolean(form.errors.state)}
                helperText={form.errors.state}
              />
            </Grid>
          </Grid>
        </Box>
        <Box component='form'>
          <Grid container spacing={3} className=' mb-[20px]'>
            <Grid item xs={12} sm={6}>
              <InputLabel>{t('properties.zipCode')}</InputLabel>
              <TextField
                name='zip_code'
                placeholder={t('properties.zipCode')}
                value={form.values.zip_code}
                onChange={form.handleChange}
                error={Boolean(form.errors.zip_code)}
                helperText={form.errors.zip_code}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <InputLabel>{t('properties.materialSaleTax')}</InputLabel>
              <TextField
                name='material_sale_tax'
                placeholder={t('properties.materialSaleTax')}
                value={form.values.material_sale_tax}
                onChange={form.handleChange}
                error={Boolean(form.errors.material_sale_tax)}
                helperText={form.errors.material_sale_tax}
              />
            </Grid>
          </Grid>
        </Box>
        <Box component='form'>
          <Box className='gap-[20px] mb-[20px]'>
            <InputLabel>
              {t('properties.tags')}
              <span className={cx('price-tag')}>{t('tagNote')}</span>
            </InputLabel>
            <div>
              <Autocomplete
                multiple
                disableClearable
                value={form.values.tags || []}
                onChange={(event, newValue) => form.setFieldValue('tags', newValue)}
                options={tags}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                popupIcon={null}
                renderTags={() => null}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant='outlined'
                    placeholder={t('searchPlaceholder')}
                    InputProps={{
                      ...params.InputProps,
                      style: {
                        paddingTop: 0,
                        paddingBottom: 0,
                      },
                      endAdornment: (
                        <>
                          <SearchIcon className='w-[16px] mr-[1px]' />
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              <div className='mt-[5px]'>
                {selectedTags.map((tag) => (
                  <Chip
                    key={tag.id}
                    label={tag.name}
                    onDelete={() => {
                      const newTags = selectedTags.filter((t) => t.id !== tag.id);
                      form.setFieldValue('tags', newTags);
                    }}
                    className={cx('tag-chip')}
                  />
                ))}
              </div>
            </div>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button color='secondary' variant='contained' onClick={handleCancel}>
          {t('cancel', { ns: 'admin/common' })}
        </Button>
        <Button
          color='primary'
          variant='contained'
          onClick={() => {
            form.handleSubmit();
            if (props.setOpenDrawer) {
              props.setOpenDrawer(false);
            }
          }}
        >
          {t('save', { ns: 'admin/common' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(EditPriceDialog);
